<template>
  <div class="navbar">
    <!-- 粒子效果 -->
    <div class="navbar-particles">
      <div v-for="i in 8" :key="i" 
           class="particle" 
           :style="getParticleStyle(i)"></div>
    </div>
    
    <div class="left-decoration">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>{{ currentRouteName }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    
    <div class="right-menu">
      <div class="timezone-wrapper">
        <el-icon class="time-icon"><Clock /></el-icon>
        <el-select 
          v-model="currentTimezone" 
          placeholder="选择时区" 
          class="timezone-select"
          @change="handleTimezoneChange"
          :teleported="false"
        >
          <el-option v-for="timezone in timezoneOptions" :key="timezone.value" :label="timezone.label"
            :value="timezone.value" />
        </el-select>
      </div>
      
      <EnvDisplay />
      
      <el-dropdown class="avatar-container" trigger="click">
        <div class="avatar-wrapper">
          <div class="avatar-box">
            <img src="@/assets/logo.png" alt="user" />
          </div>
          <span class="user-name">{{ userInfo.username }}</span>
          <el-icon class="caret"><CaretBottom /></el-icon>
        </div>
        <template #dropdown>
          <el-dropdown-menu class="user-dropdown">
            <el-dropdown-item @click="handleLogout">
              <el-icon><SwitchButton /></el-icon>
              <span>退出登录</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessageBox } from 'element-plus'
import { CaretBottom, SwitchButton, Clock } from '@element-plus/icons-vue'
import EnvDisplay from '@/components/EnvDisplay.vue'

const router = useRouter()
const route = useRoute()
const userInfo = ref(JSON.parse(localStorage.getItem('userInfo') || '{"username": "管理员"}'))

const currentTimezone = ref(8) 
const timezoneOptions = [
  { value: -11, label: 'UTC-11:00' },
  { value: -10, label: 'UTC-10:00' },
  { value: -9, label: 'UTC-09:00' },
  { value: -8, label: 'UTC-08:00' },
  { value: -7, label: 'UTC-07:00' },
  { value: -6, label: 'UTC-06:00' },
  { value: -5, label: 'UTC-05:00' },
  { value: -4, label: 'UTC-04:00' },
  { value: -3, label: 'UTC-03:00' },
  { value: -2, label: 'UTC-02:00' },
  { value: -1, label: 'UTC-01:00' },
  { value: 0, label: 'UTC+00:00' },
  { value: 1, label: 'UTC+01:00' },
  { value: 2, label: 'UTC+02:00' },
  { value: 3, label: 'UTC+03:00' },
  { value: 4, label: 'UTC+04:00' },
  { value: 5, label: 'UTC+05:00' },
  { value: 6, label: 'UTC+06:00' },
  { value: 7, label: 'UTC+07:00' },
  { value: 8, label: 'UTC+08:00' },
  { value: 9, label: 'UTC+09:00' },
  { value: 10, label: 'UTC+10:00' },
  { value: 11, label: 'UTC+11:00' },
  { value: 12, label: 'UTC+12:00' }
]

const currentRouteName = computed(() => route.meta.title || route.name)

// 粒子动画样式生成
const getParticleStyle = (index) => {
  const positions = [
    { left: '10%', top: '20%' },
    { left: '25%', top: '60%' },
    { left: '40%', top: '30%' },
    { left: '55%', top: '70%' },
    { left: '70%', top: '40%' },
    { left: '85%', top: '50%' },
    { left: '15%', top: '80%' },
    { left: '90%', top: '25%' }
  ]
  const pos = positions[(index - 1) % positions.length]
  return {
    left: pos.left,
    top: pos.top,
    animationDelay: `${(index - 1) * 0.4}s`,
    animationDuration: `${3 + (index % 3)}s`
  }
}

const handleTimezoneChange = (timezoneOffset) => {
  localStorage.setItem('userTimezone', timezoneOffset.toString())
  window.location.reload()
}

const handleLogout = () => {
  ElMessageBox.confirm('确定要退出登录吗？', '系统提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning',
    customClass: 'tech-message-box'
  }).then(() => {
    localStorage.removeItem('local_token')
    localStorage.removeItem('cn_token')
    localStorage.removeItem('global_token')
    localStorage.removeItem('userInfo')
    router.push('/login')
  })
}

onMounted(() => {
  const savedTimezone = localStorage.getItem('userTimezone')
  if (savedTimezone) {
    currentTimezone.value = parseInt(savedTimezone, 10)
  } else {
    localStorage.setItem('userTimezone', '8')
    currentTimezone.value = 8
  }
})
</script>

<style lang="scss" scoped>
.navbar {
  height: 60px;
  background: 
    linear-gradient(135deg, rgba(0, 242, 255, 0.08) 0%, rgba(168, 85, 247, 0.08) 50%, rgba(59, 130, 246, 0.08) 100%),
    rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(0, 242, 255, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 
    0 4px 20px rgba(0, 242, 255, 0.15),
    0 8px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  
  // 顶部光效
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 242, 255, 0.8), transparent);
    animation: navbarScanTop 4s ease-in-out infinite;
    box-shadow: 0 0 10px rgba(0, 242, 255, 0.6);
  }
  
  // 底部光效
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 242, 255, 0.6), rgba(168, 85, 247, 0.6), transparent);
    animation: navbarScan 5s ease-in-out infinite;
    box-shadow: 0 2px 10px rgba(0, 242, 255, 0.3);
  }
  
  // 粒子效果容器
  .navbar-particles {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
    opacity: 0.6;
    
    .particle {
      position: absolute;
      width: 2px;
      height: 2px;
      background: rgba(0, 242, 255, 0.8);
      border-radius: 50%;
      box-shadow: 0 0 4px rgba(0, 242, 255, 0.8);
      animation: particleMove 3s ease-in-out infinite;
    }
  }
}

@keyframes navbarScan {
  0% { 
    opacity: 0.3;
    transform: translateX(-100%);
  }
  50% { 
    opacity: 1;
    transform: translateX(0);
  }
  100% { 
    opacity: 0.3;
    transform: translateX(100%);
  }
}

@keyframes navbarScanTop {
  0% { 
    left: -100%;
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% { 
    left: 100%;
    opacity: 0;
  }
}

@keyframes particleMove {
  0%, 100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.3;
  }
  25% {
    transform: translate(10px, -5px) scale(1.2);
    opacity: 0.8;
  }
  50% {
    transform: translate(5px, 5px) scale(0.8);
    opacity: 1;
  }
  75% {
    transform: translate(-5px, -3px) scale(1.1);
    opacity: 0.6;
  }
}

.left-decoration {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
  animation: fadeSlideIn 0.6s ease-out;
  
  :deep(.el-breadcrumb) {
    .el-breadcrumb__item {
      .el-breadcrumb__inner {
        color: #ffffff !important;
        font-weight: 500;
        transition: all 0.3s ease;
        position: relative;
        
        &::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #00f2ff, #a855f7);
          transition: width 0.3s ease;
          box-shadow: 0 0 8px rgba(0, 242, 255, 0.6);
        }
        
        &:hover {
          color: #00f2ff !important;
          text-shadow: 0 0 10px rgba(0, 242, 255, 0.5);
          transform: translateY(-1px);
          
          &::after {
            width: 100%;
          }
        }
      }
      
      &:last-child .el-breadcrumb__inner {
        color: #00f2ff !important;
        font-weight: 600;
        text-shadow: 0 0 10px rgba(0, 242, 255, 0.4);
        animation: textGlow 2s ease-in-out infinite;
      }
    }
    
    .el-breadcrumb__separator {
      color: #94a3b8 !important;
      transition: all 0.3s ease;
      
      &:hover {
        color: #00f2ff !important;
      }
    }
  }
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes textGlow {
  0%, 100% {
    text-shadow: 0 0 10px rgba(0, 242, 255, 0.4);
  }
  50% {
    text-shadow: 0 0 20px rgba(0, 242, 255, 0.6), 0 0 30px rgba(0, 242, 255, 0.3);
  }
}

.right-menu {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 20px;
  position: relative;
  z-index: 1;
  animation: fadeSlideIn 0.8s ease-out;
  animation-delay: 0.2s;
  animation-fill-mode: both;

  .timezone-wrapper {
    display: flex;
    align-items: center;
    background: 
      linear-gradient(135deg, rgba(0, 242, 255, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%),
      rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    padding: 6px 12px;
    border: 1px solid rgba(0, 242, 255, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 242, 255, 0.1);
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(0, 242, 255, 0.2), transparent);
      transition: left 0.5s;
    }
    
    &:hover {
      background: 
        linear-gradient(135deg, rgba(0, 242, 255, 0.12) 0%, rgba(168, 85, 247, 0.12) 100%),
        rgba(255, 255, 255, 0.08);
      border-color: rgba(0, 242, 255, 0.4);
      box-shadow: 
        0 4px 12px rgba(0, 242, 255, 0.2),
        0 0 20px rgba(0, 242, 255, 0.1);
      transform: translateY(-2px);
      
      &::before {
        left: 100%;
      }
    }
    
    .time-icon {
      color: #00f2ff;
      font-size: 16px;
      margin-right: 8px;
      filter: drop-shadow(0 0 4px rgba(0, 242, 255, 0.5));
    }
    
    .timezone-select {
      width: 130px;
      :deep(.el-input__wrapper) {
        background-color: transparent !important;
        box-shadow: none !important;
        padding: 0;
      }
      :deep(.el-input__inner) {
        color: #e2e8f0 !important;
        font-size: 13px;
        font-weight: 500;
      }
    }
  }

  .avatar-container {
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      inset: -4px;
      background: linear-gradient(45deg, #00f2ff, #a855f7, #00f2ff);
      background-size: 200% 200%;
      border-radius: 28px;
      opacity: 0;
      transition: opacity 0.3s;
      animation: gradientRotate 3s linear infinite;
      z-index: -1;
    }
    
    &:hover {
      transform: translateY(-2px);
      
      &::before {
        opacity: 0.6;
      }
      
      .avatar-wrapper {
        background: linear-gradient(135deg, rgba(0, 242, 255, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%);
        border-color: rgba(0, 242, 255, 0.5);
        box-shadow: 
          0 0 20px rgba(0, 242, 255, 0.3),
          0 4px 12px rgba(0, 242, 255, 0.2);
        
        .avatar-box {
          animation: avatarPulse 0.6s ease;
        }
      }
    }

    .avatar-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      background: linear-gradient(135deg, rgba(0, 242, 255, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%);
      padding: 4px 12px 4px 4px;
      border-radius: 24px;
      border: 1px solid rgba(0, 242, 255, 0.3);
      box-shadow: 
        0 0 15px rgba(0, 242, 255, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
      transition: all 0.3s ease;

      .avatar-box {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: 
          radial-gradient(circle, rgba(0, 242, 255, 0.2) 0%, transparent 70%),
          #1e293b;
        border: 2px solid rgba(0, 242, 255, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        box-shadow: 
          0 0 10px rgba(0, 242, 255, 0.3),
          inset 0 0 10px rgba(0, 242, 255, 0.1);
        
        img {
          width: 20px;
          height: 20px;
          filter: drop-shadow(0 0 4px rgba(0, 242, 255, 0.5));
        }
      }

      .user-name {
        font-size: 14px;
        color: #ffffff;
        font-weight: 600;
        text-shadow: 0 0 10px rgba(0, 242, 255, 0.3);
        letter-spacing: 0.5px;
      }
      
      .caret {
        color: #00f2ff;
        font-size: 12px;
        filter: drop-shadow(0 0 4px rgba(0, 242, 255, 0.5));
      }
    }
  }
}

@keyframes gradientRotate {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes avatarPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style> 